(function () {
  'use strict';

  var math = {
    ascending: function (thresholds) {
      return thresholds.slice().sort(function (a, b) { return a - b; });
    },

    MIN_MARKER_GAP: 9,

    markerPositions: function (thresholds) {
      var sorted = this.ascending(thresholds);
      var count = sorted.length;
      if (!count) return [];

      var top = sorted[count - 1];
      if (top <= 0) return sorted.map(function () { return 0; });

      var proportional = sorted.map(function (threshold) { return (threshold / top) * 100; });
      var equalGap = 100 / count;

      var tightest = Infinity;
      for (var i = 1; i < count; i++) {
        tightest = Math.min(tightest, proportional[i] - proportional[i - 1]);
      }

      var blend = 1;
      if (count > 1 && tightest < this.MIN_MARKER_GAP) {
        blend = (equalGap - this.MIN_MARKER_GAP) / (equalGap - tightest);
        blend = Math.max(0, Math.min(1, blend));
      }

      return proportional.map(function (position, index) {
        var even = (index + 1) * equalGap;
        return Math.round((blend * position + (1 - blend) * even) * 100) / 100;
      });
    },

    parseMoney: function (text, format) {
      var placeholder = (String(format || '').match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
      var digits = String(text == null ? '' : text).replace(/\D/g, '');
      if (!digits) return null;
      var value = parseInt(digits, 10);
      return placeholder.indexOf('no_decimals') !== -1 ? value * 100 : value;
    },

    marketRate: function (shopifyCurrency, shopCurrency, cartCurrency) {
      if (!cartCurrency || cartCurrency === shopCurrency) return 1;
      if (!shopifyCurrency || shopifyCurrency.active !== cartCurrency) return null;
      var rate = parseFloat(shopifyCurrency.rate);
      return rate > 0 ? rate : null;
    },

    displayThreshold: function (thresholdCents, rate) {
      return Math.floor(thresholdCents * (rate || 1));
    },

    eligibleTotal: function (items, excludedIds, fallbackTotal) {
      if (!items || !items.length) return fallbackTotal || 0;
      var excluded = (excludedIds || []).map(String);
      return items.reduce(function (sum, item) {
        if (excluded.indexOf(String(item.product_id)) !== -1) return sum;
        return sum + (parseInt(item.final_line_price, 10) || 0);
      }, 0);
    },

    isUnlocked: function (cartTotalCents, thresholdCents, rate) {
      return Math.floor((cartTotalCents / (rate || 1)) / thresholdCents) >= 1;
    },

    isReplaced: function (cartTotalCents, maxCents, rate) {
      if (!maxCents || maxCents <= 0) return false;
      return (cartTotalCents / (rate || 1)) >= maxCents;
    },

    sortTiers: function (thresholds, maxes) {
      var pairs = thresholds.map(function (threshold, index) {
        return { min: threshold, max: (maxes && maxes[index]) || null };
      }).sort(function (a, b) { return a.min - b.min; });
      return {
        thresholds: pairs.map(function (pair) { return pair.min; }),
        maxes: pairs.map(function (pair) { return pair.max; })
      };
    },

    projectedState: function (cartTotalCents, buttonCents, thresholds, rate, maxes, layout) {
      if (!buttonCents || buttonCents <= 0) return null;
      return this.computeState(cartTotalCents + buttonCents, thresholds, rate, maxes, layout);
    },

    tierState: function (index, state, projected) {
      var real = state.tiers ? state.tiers[index] : (index < state.unlocked ? 'unlocked' : 'locked');
      var next = projected ? (projected.tiers ? projected.tiers[index] : (index < projected.unlocked ? 'unlocked' : 'locked')) : null;

      if (real === 'replaced' || real === 'unlocked') return real;
      return next === 'unlocked' ? 'projected' : 'locked';
    },

    layoutPositions: function (sorted, tiers) {
      var shown = sorted.filter(function (_, index) { return tiers[index] !== 'replaced'; });
      var positions = this.markerPositions(shown);
      var cursor = 0;
      return tiers.map(function (tier) { return tier === 'replaced' ? null : positions[cursor++]; });
    },

    computeState: function (cartTotalCents, thresholds, rate, maxes, layout) {
      var count = thresholds.length;
      rate = rate || 1;

      if (!count) {
        return { index: 0, unlocked: 0, held: 0, visible: 0, tiers: [], positions: [], nextThreshold: null, remainingCents: null, progress: 0 };
      }

      var self = this;
      var sortedTiers = this.sortTiers(thresholds, maxes);
      var sorted = sortedTiers.thresholds;
      var tiers = sorted.map(function (threshold, index) {
        if (!self.isUnlocked(cartTotalCents, threshold, rate)) return 'locked';
        return self.isReplaced(cartTotalCents, sortedTiers.maxes[index], rate) ? 'replaced' : 'unlocked';
      });
      var unlocked = tiers.filter(function (tier) { return tier !== 'locked'; }).length;
      var held = tiers.filter(function (tier) { return tier === 'unlocked'; }).length;
      var positions = layout || this.layoutPositions(sorted, tiers);
      var visible = tiers.filter(function (tier) { return tier !== 'replaced'; }).length;

      if (unlocked >= count) {
        return { index: count, unlocked: count, held: held, visible: visible, tiers: tiers, positions: positions, nextThreshold: null, remainingCents: null, progress: 100 };
      }

      var previousIndex = -1;
      for (var i = unlocked - 1; i >= 0; i--) {
        if (positions[i] !== null && positions[i] !== undefined) { previousIndex = i; break; }
      }
      var previousValue = previousIndex < 0 ? 0 : sorted[previousIndex] * rate;
      var previousPosition = previousIndex < 0 ? 0 : positions[previousIndex];
      var nextValue = sorted[unlocked] * rate;
      var span = nextValue - previousValue;
      var within = span > 0 ? (cartTotalCents - previousValue) / span : 0;
      within = Math.min(Math.max(within, 0), 1);
      var progress = previousPosition + within * (positions[unlocked] - previousPosition);

      return {
        index: unlocked,
        unlocked: unlocked,
        held: held,
        visible: visible,
        tiers: tiers,
        positions: positions,
        nextThreshold: this.displayThreshold(sorted[unlocked], rate),
        remainingCents: Math.max(Math.floor(nextValue - cartTotalCents), 0),
        progress: Math.round(progress * 100) / 100
      };
    }
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = math;
  }

  if (typeof customElements === 'undefined') return;

  function moneyFormat(withCurrency) {
    var settings = (window.theme && window.theme.settings) || {};
    var fallback = (window.themeVariables && window.themeVariables.settings) || {};
    if (withCurrency && settings.currencyCodeEnabled) {
      return settings.moneyWithCurrencyFormat || fallback.moneyWithCurrencyFormat;
    }
    return settings.moneyFormat || fallback.moneyFormat;
  }

  function formatMoney(cents, withoutTrailingZeros) {
    var format = moneyFormat(false);
    var formatted;

    if (window.theme && window.theme.Currency && format) {
      formatted = window.theme.Currency.formatMoney(cents, format);
    } else {
      formatted = '$' + (cents / 100).toFixed(2);
    }

    if (withoutTrailingZeros) formatted = formatted.replace(/([.,])00(?!\d)/, '');
    return formatted;
  }

  class PdpFreebies extends HTMLElement {
    connectedCallback() {
      var configNode = this.querySelector('[data-freebies-config]');
      if (!configNode) return;

      this.config = JSON.parse(configNode.textContent);
      this.cartTotal = this.config.cartTotal;

      this.rate = math.marketRate(window.Shopify && window.Shopify.currency, this.config.shopCurrency, this.config.cartCurrency);
      if (this.rate === null) return;

      this.headline = this.querySelector('[data-freebies-headline]');
      this.subline = this.querySelector('[data-freebies-subline]');
      this.status = this.querySelector('[data-freebies-status]');
      this.rail = this.querySelector('[data-freebies-rail]');
      this.stops = Array.prototype.slice.call(this.querySelectorAll('[data-freebies-stop]'));
      this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-freebies-row]'));

      if (this.rate !== 1) this.applyMarketThresholds();

      this.subscribe();

      this.buttonCents = null;
      if (this.config.preview) this.watchBuyPrice();

      this.onWake = this.refresh.bind(this);
      window.addEventListener('pageshow', this.onWake);
      document.addEventListener('visibilitychange', this.onWake);

      this.lastKey = null;
      this.render();
      this.removeAttribute('hidden');
    }

    applyMarketThresholds() {
      var rate = this.rate;
      var thresholds = this.config.thresholds;
      var unlocksAt = this.config.unlocksAtLabel || '[amount]';

      this.stops.forEach(function (stop, index) {
        var price = stop.querySelector('[data-freebies-stop-price]');
        if (price) price.textContent = formatMoney(math.displayThreshold(thresholds[index], rate), true);
      });

      this.rows.forEach(function (row, index) {
        var meta = row.querySelector('[data-freebies-row-meta]');
        if (meta) meta.textContent = unlocksAt.split('[amount]').join(formatMoney(math.displayThreshold(thresholds[index], rate), true));
      });
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onWake);
      document.removeEventListener('visibilitychange', this.onWake);
      this.unsubscribe?.();
      this.buyObserver?.disconnect();
    }

    // Mirrors whatever the buy button currently says (bundle deals, routine picks, variant price)
    // and previews how far that would move the bar. Reading the button text is deliberate:
    // Kaching Bundles only exposes its discounted deal price by writing it there.
    watchBuyPrice() {
      var container = this.config.buyPriceId && document.getElementById(this.config.buyPriceId);
      if (!container) return;

      var self = this;
      var read = function () {
        var node = container.hasAttribute('hidden') ? null : container.querySelector('product-buy-price');
        self.buttonCents = node ? math.parseMoney(node.textContent, moneyFormat(true)) : null;
        self.render();
      };

      if (this.config.productExcluded) return;

      this.buyObserver = new MutationObserver(read);
      this.buyObserver.observe(container, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden']
      });
      read();
    }


    subscribe() {
      var pubsub = window.theme && window.theme.pubsub;
      if (!pubsub) return;

      var self = this;

      this.unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        if (payload?.cart && typeof payload.cart.total_price === 'number') {
          self.cartTotal = math.eligibleTotal(payload.cart.items, self.config.excludedProducts, payload.cart.total_price);
          self.render();
        } else {
          self.refresh();
        }
      });
    }


    refresh() {
      if (document.visibilityState === 'hidden') return;

      var theme = window.theme || {};
      var url = theme.routes?.cart_url || '/cart';
      var config = theme.utils?.fetchConfig
        ? theme.utils.fetchConfig('json', 'GET')
        : { headers: { Accept: 'application/json' } };
      var self = this;

      fetch(url, config)
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          if (cart && typeof cart.total_price === 'number') {
            self.cartTotal = math.eligibleTotal(cart.items, self.config.excludedProducts, cart.total_price);
            self.render();
          }
        })
        .catch(function () { /* leave the last known total on screen */ });
    }

    render() {
      var total = this.cartTotal;
      var thresholds = this.config.thresholds;
      var rate = this.rate;
      var maxes = this.config.maxes || [];
      var state = math.computeState(total, thresholds, rate, maxes);
      var projected = this.config.preview ? math.projectedState(total, this.buttonCents, thresholds, rate, maxes, state.positions) : null;

      var key = total + ':' + (projected ? projected.progress + '/' + projected.tiers.join('') : '');
      if (key === this.lastKey) return;
      this.lastKey = key;
      var complete = state.unlocked >= thresholds.length;
      var positions = state.positions;
      var remaining = state.remainingCents === null ? '' : formatMoney(state.remainingCents);


      function fill(text) {
        return (text || '')
          .split('[amount]').join(remaining)
          .split('[unlocked]').join(state.held)
          .split('[total]').join(state.visible);
      }

      var headline = complete ? this.config.headlineComplete : this.config.headline;
      var subline = complete ? this.config.sublineComplete : this.config.subline;

      this.dataset.unlocked = String(state.unlocked);
      this.dataset.held = String(state.held);

      if (this.headline) this.headline.textContent = fill(headline);
      if (this.subline) {
        this.subline.textContent = fill(subline);
        this.subline.hidden = !subline;
      }
      if (this.status) this.status.textContent = fill(this.config.statusLabel);
      if (this.rail) {
        this.rail.style.setProperty('--progress', state.progress + '%');
        this.rail.style.setProperty('--projected', (projected ? projected.progress : state.progress) + '%');
      }
      this.dataset.projected = projected ? String(projected.unlocked) : '';

      this.stops.forEach(function (stop, index) {
        stop.dataset.state = math.tierState(index, state, projected);
        if (positions[index] !== null && positions[index] !== undefined) stop.style.left = positions[index] + '%';
      });

      // Gift rows follow the real cart only; the preview lives on the rail and its stops.
      var addLabel = this.config.addLabel;
      this.rows.forEach(function (row, index) {
        var tier = state.tiers[index];
        row.dataset.state = tier;

        var note = row.querySelector('[data-freebies-row-note]');
        if (!note) return;

        if (tier === 'replaced') {
          return; // the row is hidden
        } else if (tier === 'unlocked') {
          note.textContent = note.dataset.includedLabel;
        } else {
          note.textContent = (addLabel || '[amount]')
            .split('[amount]')
            .join(formatMoney(Math.max(Math.floor(thresholds[index] * rate - total), 0)));
        }
      });
    }
  }

  customElements.define('pdp-freebies', PdpFreebies);
})();
